import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { SkeletonModule } from 'primeng/skeleton';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { IftaLabelModule } from 'primeng/iftalabel';
import { AccountsService } from '../accounts.service';
import { ZatcaBusinessRegistration, ZatcaOnboardingStatus } from '../accounting-compliance.model';
import { SessionStorageService } from '../../services/session-storage.service';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';

@Component({
    selector: 'app-zatca-onboarding',
    standalone: true,
    imports: [
        CommonModule, FormsModule, ButtonModule, InputTextModule,
        SelectModule, TagModule, ToastModule, SkeletonModule,
        InputGroupModule, InputGroupAddonModule, IftaLabelModule,
        PageShellComponent, PageHeroComponent, PageDataPanelComponent,
        PageFormSectionComponent
    ],
    providers: [MessageService],
    template: `
        <p-toast></p-toast>
        <app-page-shell>
            <app-page-hero eyebrow="ZATCA E-Invoicing" title="ZATCA Onboarding"
                subtitle="Register your company for Saudi ZATCA e-invoicing compliance."
                icon="pi pi-shield">
                <div actions>
                    <p-button *ngIf="!loadingStatus && !status?.onboarded && !showForm"
                              label="Start Onboarding" icon="pi pi-plus" (click)="showForm = true"></p-button>
                    <p-button label="Refresh Status" icon="pi pi-refresh" (click)="checkStatus()"
                              [loading]="loadingStatus" severity="secondary"></p-button>
                </div>
            </app-page-hero>

            <!-- Loading State -->
            @if (loadingStatus) {
            <app-page-data-panel title="Checking Status">
                <div class="p-4">
                    <p-skeleton width="60%" height="1.5rem" styleClass="mb-2"></p-skeleton>
                    <p-skeleton width="40%" height="1rem"></p-skeleton>
                </div>
            </app-page-data-panel>
            }

            <!-- Onboarding Status Display -->
            @if (!loadingStatus && status) {
            <app-page-data-panel title="Onboarding Status">
                <div class="status-banner" [class.onboarded]="status.onboarded" [class.not-onboarded]="!status.onboarded">
                    <div class="status-icon">
                        <i [class]="status.onboarded ? 'pi pi-check-circle' : 'pi pi-exclamation-circle'"></i>
                    </div>
                    <div class="status-text">
                        <h3>{{ status.onboarded ? 'Onboarded' : 'Not Onboarded' }}</h3>
                        <p>{{ status.onboarded
                            ? 'Your company is registered with ZATCA. You can now generate e-invoices.'
                            : 'Your company has not been onboarded to ZATCA yet. Click "Start Onboarding" above to begin.' }}</p>
                    </div>
                </div>
                @if (status.error) {
                <div class="status-error">
                    <i class="pi pi-exclamation-triangle"></i> {{ status.error }}
                </div>
                }
            </app-page-data-panel>
            }

            <!-- Onboarding Form -->
            @if (showForm) {
            <form #onboardingForm="ngForm" (ngSubmit)="submitOnboarding()">
                <div class="onboarding-form-layout">
                <app-page-form-section
                    icon="fa-solid fa-building"
                    title="Company Information"
                    subtitle="Legal business details required for ZATCA registration.">
                    <div class="formgrid grid">
                        <div class="field col-12 md:col-6 xl:col-4">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="fa-solid fa-building"></i></p-inputGroupAddon>
                                <p-iftaLabel>
                                    <input id="companyName" pInputText
                                           [(ngModel)]="registration.companyName" name="companyName" required
                                           [disabled]="formDisabled" />
                                    <label for="companyName">Company Name <span class="required">*</span></label>
                                </p-iftaLabel>
                            </p-inputGroup>
                        </div>
                        <div class="field col-12 md:col-6 xl:col-4">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="fa-solid fa-receipt"></i></p-inputGroupAddon>
                                <p-iftaLabel>
                                    <input id="vatNumber" pInputText
                                           [(ngModel)]="registration.vatNumber" name="vatNumber" required
                                           placeholder="e.g. 312345678900003"
                                           [disabled]="formDisabled" />
                                    <label for="vatNumber">VAT Number <span class="required">*</span></label>
                                </p-iftaLabel>
                            </p-inputGroup>
                        </div>
                        <div class="field col-12 md:col-6 xl:col-4">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="fa-solid fa-file-lines"></i></p-inputGroupAddon>
                                <p-iftaLabel>
                                    <input id="crNumber" pInputText
                                           [(ngModel)]="registration.crNumber" name="crNumber" required
                                           placeholder="e.g. 1012345678"
                                           [disabled]="formDisabled" />
                                    <label for="crNumber">CR Number <span class="required">*</span></label>
                                </p-iftaLabel>
                            </p-inputGroup>
                        </div>
                        <div class="field col-12 md:col-6 xl:col-4">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="fa-solid fa-briefcase"></i></p-inputGroupAddon>
                                <p-iftaLabel>
                                    <p-select id="industry" [options]="industryOptions"
                                              [(ngModel)]="registration.industry" name="industry"
                                              optionLabel="label" optionValue="value" required
                                              placeholder="Select industry" styleClass="w-full"
                                              [disabled]="formDisabled">
                                    </p-select>
                                    <label for="industry">Industry <span class="required">*</span></label>
                                </p-iftaLabel>
                            </p-inputGroup>
                        </div>
                        <div class="field col-12 md:col-6 xl:col-4">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="fa-solid fa-file-invoice"></i></p-inputGroupAddon>
                                <p-iftaLabel>
                                    <p-select id="invoiceType" [options]="invoiceTypeOptions"
                                              [(ngModel)]="registration.invoiceType" name="invoiceType"
                                              optionLabel="label" optionValue="value" required
                                              placeholder="Select types" styleClass="w-full"
                                              [disabled]="formDisabled">
                                    </p-select>
                                    <label for="invoiceType">Invoice Type <span class="required">*</span></label>
                                </p-iftaLabel>
                            </p-inputGroup>
                        </div>
                        <div class="field col-12 md:col-6 xl:col-4">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="fa-solid fa-sitemap"></i></p-inputGroupAddon>
                                <p-iftaLabel>
                                    <input id="organizationUnit" pInputText
                                           [(ngModel)]="registration.organizationUnitName" name="organizationUnit"
                                           placeholder="e.g. Finance Department"
                                           [disabled]="formDisabled" />
                                    <label for="organizationUnit">Organization Unit</label>
                                </p-iftaLabel>
                            </p-inputGroup>
                        </div>
                        <div class="field col-12">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="fa-solid fa-location-dot"></i></p-inputGroupAddon>
                                <p-iftaLabel>
                                    <input id="address" pInputText
                                           [(ngModel)]="registration.address" name="address" required
                                           placeholder="Full business address in Saudi Arabia"
                                           [disabled]="formDisabled" />
                                    <label for="address">Business Address <span class="required">*</span></label>
                                </p-iftaLabel>
                            </p-inputGroup>
                        </div>
                    </div>
                </app-page-form-section>

                <app-page-form-section
                    icon="fa-solid fa-address-card"
                    title="Contact Information"
                    subtitle="Primary contact person for ZATCA communications.">
                    <div class="formgrid grid">
                        <div class="field col-12 md:col-6 xl:col-4">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="fa-solid fa-user"></i></p-inputGroupAddon>
                                <p-iftaLabel>
                                    <input id="contactPerson" pInputText
                                           [(ngModel)]="registration.contactPerson" name="contactPerson" required
                                           placeholder="Full name"
                                           [disabled]="formDisabled" />
                                    <label for="contactPerson">Contact Person <span class="required">*</span></label>
                                </p-iftaLabel>
                            </p-inputGroup>
                        </div>
                        <div class="field col-12 md:col-6 xl:col-4">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="fa-solid fa-envelope"></i></p-inputGroupAddon>
                                <p-iftaLabel>
                                    <input id="contactEmail" type="email" pInputText
                                           [(ngModel)]="registration.contactEmail" name="contactEmail" required email
                                           placeholder="email@company.com"
                                           [disabled]="formDisabled" />
                                    <label for="contactEmail">Contact Email <span class="required">*</span></label>
                                </p-iftaLabel>
                            </p-inputGroup>
                        </div>
                    </div>
                </app-page-form-section>

                <app-page-form-section
                    icon="fa-solid fa-key"
                    title="ZATCA Authentication"
                    subtitle="Enter the OTP obtained from the ZATCA portal to complete the registration.">
                    <div class="formgrid grid">
                        <div class="field col-12 md:col-6">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="fa-solid fa-shield-keyhole"></i></p-inputGroupAddon>
                                <p-iftaLabel>
                                    <input id="otp" pInputText
                                           [(ngModel)]="registration.otp" name="otp" required
                                           placeholder="Enter OTP from ZATCA portal"
                                           [disabled]="formDisabled" />
                                    <label for="otp">ZATCA OTP <span class="required">*</span></label>
                                </p-iftaLabel>
                            </p-inputGroup>
                            <small class="field-hint">Obtain this from the ZATCA portal after registering your company.</small>
                        </div>
                    </div>
                </app-page-form-section>

                </div><!-- /.onboarding-form-layout -->

                <div class="form-action-bar">
                    <p-button label="Cancel" severity="secondary" (click)="showForm = false"
                              [disabled]="submitting || formDisabled"></p-button>
                    <p-button *ngIf="!formDisabled" label="Submit Onboarding" icon="pi pi-check" type="submit"
                              [disabled]="!onboardingForm.valid || submitting" [loading]="submitting"></p-button>
                </div>
            </form>
            }

            <!-- Onboarding Result -->
            @if (onboardingResult || onboardingError) {
            <app-page-data-panel title="Onboarding Result">
                @if (onboardingResult && !onboardingError) {
                <div class="result-card success">
                    <i class="pi pi-check-circle result-icon"></i>
                    <h3>ZATCA Onboarding Successful!</h3>
                    <p>Your company is now registered with ZATCA. You can proceed to generate e-invoices.</p>
                    @if (onboardingResult.dispositionMessage) {
                    <div class="result-details">
                        <strong>Message:</strong> {{ onboardingResult.dispositionMessage }}
                    </div>
                    }
                </div>
                }
                @if (onboardingError) {
                <div class="result-card error">
                    <i class="pi pi-times-circle result-icon"></i>
                    <h3>Onboarding Failed</h3>
                    <p>{{ onboardingError }}</p>
                </div>
                }
                <div class="form-action-bar" style="margin-top: 1rem;">
                    <p-button label="Done" icon="pi pi-check" (click)="finishOnboarding()"></p-button>
                </div>
            </app-page-data-panel>
            }
        </app-page-shell>
    `,
    styles: [`
        .status-banner {
            display: flex;
            align-items: center;
            gap: 1rem;
            padding: 1.5rem;
            border-radius: 8px;
            margin-bottom: 1rem;
        }
        .status-banner.onboarded {
            background: #e8f5e9;
            border: 1px solid #a5d6a7;
        }
        .status-banner.not-onboarded {
            background: #fff8e1;
            border: 1px solid #ffe082;
        }
        .status-icon i {
            font-size: 2.5rem;
        }
        .status-banner.onboarded .status-icon i { color: #2e7d32; }
        .status-banner.not-onboarded .status-icon i { color: #f57f17; }
        .status-text h3 {
            margin: 0 0 0.25rem;
            font-size: 1.25rem;
        }
        .status-text p {
            margin: 0;
            color: #666;
        }
        .status-error {
            padding: 0.75rem;
            background: #ffebee;
            border: 1px solid #ef9a9a;
            border-radius: 6px;
            color: #c62828;
            margin-top: 0.75rem;
        }
        .required { color: #e53935; }
        .field-hint {
            display: block;
            margin-top: 0.35rem;
            margin-left: 0.25rem;
            color: #888;
            font-size: 0.8rem;
        }
        .form-action-bar {
            display: flex;
            justify-content: flex-end;
            gap: 0.75rem;
            padding: 1rem 0;
        }
        .onboarding-form-layout {
            display: flex;
            flex-direction: column;
            gap: 1rem;
            min-width: 0;
        }
        .result-card {
            text-align: center;
            padding: 2rem;
            border-radius: 8px;
        }
        .result-card.success { background: #e8f5e9; border: 1px solid #a5d6a7; }
        .result-card.error { background: #ffebee; border: 1px solid #ef9a9a; }
        .result-icon { font-size: 3rem; margin-bottom: 0.75rem; }
        .result-card.success .result-icon { color: #2e7d32; }
        .result-card.error .result-icon { color: #c62828; }
        .result-card h3 { margin: 0 0 0.5rem; }
        .result-card p { margin: 0 0 0.5rem; color: #555; }
        .result-details { margin-top: 0.75rem; font-size: 0.9rem; color: #333; }

        /* PrimeNG fix: ensure p-select fills width inside p-inputGroup */
        .p-inputgroup p-select {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;
        }
        .p-inputgroup p-inputNumber {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;
        }
        :host ::ng-deep .p-inputgroup > .p-inputwrapper:last-child > .p-component {
            border-top-left-radius: 0 !important;
            border-bottom-left-radius: 0 !important;
        }
    `]
})
export class ZatcaOnboardingComponent implements OnInit {
    companyId = 0;
    companyName = '';
    loadingStatus = false;
    submitting = false;
    showForm = false;
    status: ZatcaOnboardingStatus | null = null;
    onboardingResult: any = null;
    onboardingError = '';

    registration: ZatcaBusinessRegistration = {
        owningCompanyId: 0,
        companyName: '',
        vatNumber: '',
        crNumber: '',
        address: '',
        otp: '',
        contactPerson: '',
        contactEmail: '',
        organizationUnitName: '',
        invoiceType: '1000',
        industry: 'Transportation'
    };

    industryOptions = [
        { label: 'Transportation & Logistics', value: 'Transportation' },
        { label: 'Warehousing & Storage', value: 'Warehousing' },
        { label: 'Freight Forwarding', value: 'Freight Forwarding' },
        { label: 'Shipping & Maritime', value: 'Shipping' },
        { label: 'Courier & Parcel', value: 'Courier' },
        { label: 'Other', value: 'Other' }
    ];

    invoiceTypeOptions = [
        { label: 'B2B Only', value: '1000' },
        { label: 'B2B + B2C', value: '1100' }
    ];

    constructor(
        private accountsService: AccountsService,
        private sessionStorageService: SessionStorageService,
        private messageService: MessageService
    ) {}

    get formDisabled(): boolean {
        return this.status?.onboarded === true;
    }

    ngOnInit(): void {
        const company = this.sessionStorageService.getCompany();
        this.companyId = company?.id ?? 0;
        this.companyName = company?.name ?? '';
        this.registration.owningCompanyId = this.companyId;

        // Prefill from current company profile
        this.registration.companyName = company?.name ?? '';
        this.registration.crNumber = company?.crNumber?.toString() ?? '';
        this.registration.vatNumber = company?.taxRegNumber ?? '';
        this.registration.address = company?.address ?? '';
        this.registration.contactEmail = company?.emailAddress ?? '';

        this.checkStatus();
    }

    checkStatus(): void {
        if (!this.companyId) return;
        this.loadingStatus = true;
        this.accountsService.getZatcaOnboardingStatus(this.companyId).subscribe({
            next: status => {
                this.status = status;
                this.loadingStatus = false;
            },
            error: () => {
                this.status = { onboarded: false, error: 'Could not check onboarding status.' };
                this.loadingStatus = false;
            }
        });
    }

    submitOnboarding(): void {
        this.submitting = true;
        this.onboardingResult = null;
        this.onboardingError = '';

        this.accountsService.onboardZatca(this.registration).subscribe({
            next: response => {
                this.onboardingResult = response;
                this.submitting = false;
                // Refresh status
                this.checkStatus();
                this.messageService.add({
                    severity: 'success',
                    summary: 'Onboarding Complete',
                    detail: 'Your company has been successfully onboarded to ZATCA.'
                });
            },
            error: err => {
                this.submitting = false;
                this.onboardingError = err.error?.message || err.message || 'Onboarding failed. Please try again.';
                this.messageService.add({
                    severity: 'error',
                    summary: 'Onboarding Failed',
                    detail: this.onboardingError
                });
            }
        });
    }

    finishOnboarding(): void {
        this.showForm = false;
        this.onboardingResult = null;
        this.onboardingError = '';
        this.checkStatus();
    }
}
